import { useMemo, useState } from "react";

import { getEmployeeList } from "src/apis/employeeApi";
import { BRDiv, defaultFormText, titleMRClass } from "src/components/common/common";
import { CodeForm } from "src/components/common/form/CodeForm";
import { InputForm } from "src/components/common/form/input/InputForm";
import { SingleDateForm } from "src/components/common/form/SingleDateForm";
import { useDescText } from "src/hooks/useDescText";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import useUiuxStore from "src/store/useUiuxStore";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import { formatDate } from "src/utils/string/stringUtil";
import { DESC_FLD } from "src/utils/descLanguage/descLanguageUtil";

import { defaultKSK2140_R01InfoDto } from "./Dto_KSK2140_R01";
import { useKSK2140_R01Store } from "./Store_KSK2140_R01";

export const Info_KSK2140_R01 = () => {
	const { setCodeSearchModalProps } = useUiuxStore();
	const { renderDescText, getFormTitleWidth } = useDescText();
	const {
		selectedKSK2140_R01List,
		editKSK2140_R01,
		setEditKSK2140_R01,
	} = useKSK2140_R01Store();
	const [inspectDateVisible, setInspectDateVisible] = useState(false);
	const [inspectDatePickerPos, setInspectDatePickerPos] = useState({
		top: 0,
		left: 0,
		height: 0,
	});
	const formTitleWidth = useMemo(() => getFormTitleWidth([
		renderDescText(DESC_FLD.ITEM_CODE, "품목코드"),
		renderDescText(DESC_FLD.INSPECTION_DATE, "검사일자"),
		renderDescText(DESC_FLD.AUTO_LABEL_PRINT, "라벨자동출력"),
		renderDescText(DESC_FLD.INSPECTOR, "검사자"),
		renderDescText(DESC_FLD.APPROVER, "승인자"),
	]), [getFormTitleWidth, renderDescText]);

	const infoFieldConfigs = useMemo<InfoCodeSearchFieldConfig[]>(
		() => [
			{
				fieldName: "inspectorEmp",
				category: "담당자",
				focusId: "KSK2140_R01-inspector",
				responseMapping: { empNo: "inspectorEmp", empNm: "inspectorEmpNm" },
				searchFunction: async (code: string) => {
					const response = await getEmployeeList("all");
					return response.data.data.find((employee) => employee.empNo === code);
				},
			},
			{
				fieldName: "approvorEmpno",
				category: "담당자",
				focusId: "KSK2140_R01-approver",
				responseMapping: { empNo: "approvorEmpno", empNm: "approvorEmpNm" },
				searchFunction: async (code: string) => {
					const response = await getEmployeeList("all");
					return response.data.data.find((employee) => employee.empNo === code);
				},
			},
		],
		[],
	);

	const { getFieldHandlers } = useInfoFormEvents({
		editData: editKSK2140_R01,
		setEditData: setEditKSK2140_R01,
		defaultData: defaultKSK2140_R01InfoDto,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: "KSK2140_R01",
	});

	const isEditable = selectedKSK2140_R01List.length > 0;
	const firstSelected = selectedKSK2140_R01List[0];
	const labelPrintAutoTitle = renderDescText(DESC_FLD.AUTO_LABEL_PRINT, "라벨자동출력");
	const labelPrintAutoValue = editKSK2140_R01.labelPrintAuto === "Y" ? "Y" : "N";

	return (
		<div className="w-full h-full flex flex-wrap content-start pt-[12px] pl-[12px] pr-[12px] pb-[6px] overflow-hidden border border-gray-300 bg-white">
			<InputForm
				title={renderDescText(DESC_FLD.ITEM_CODE, "품목코드")}
				titleWidth={formTitleWidth}
				name="itemCd"
				value={editKSK2140_R01.itemCd || firstSelected?.itemCd || ""}
				editable={false}
			/>
			<SingleDateForm
				title={renderDescText(DESC_FLD.INSPECTION_DATE, "검사일자")}
				titleWidth={formTitleWidth}
				value={editKSK2140_R01.inspectDt ? new Date(editKSK2140_R01.inspectDt) : undefined}
				setValue={(inspectDt) =>
					setEditKSK2140_R01({ ...editKSK2140_R01, inspectDt: formatDate(inspectDt) })
				}
				editable={isEditable}
				visible={inspectDateVisible}
				setVisible={setInspectDateVisible}
				pickerPos={inspectDatePickerPos}
				setPickerPos={setInspectDatePickerPos}
			/>
			<div
				className={`flex items-center ${defaultFormText} mr-[10px] mb-[5px]`}
				style={{ width: "max-content" }}
			>
				<p
					className={`form-title ${titleMRClass}`}
					style={{
						width: formTitleWidth,
						minWidth: formTitleWidth,
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
					title={labelPrintAutoTitle}
				>
					{labelPrintAutoTitle}
				</p>
				<button
					type="button"
					className="flex h-[34px] w-[76px] min-w-[76px] items-center justify-between rounded-full border border-[#c7cfda] bg-transparent p-[3px] text-[14px] font-bold leading-none"
					onClick={() =>
						setEditKSK2140_R01({
							...editKSK2140_R01,
							labelPrintAuto: labelPrintAutoValue === "Y" ? "N" : "Y",
						})
					}
					aria-pressed={labelPrintAutoValue === "Y"}
				>
					<span className={`flex h-[28px] w-[28px] items-center justify-center rounded-full ${labelPrintAutoValue === "Y" ? "bg-[#2f67ec] text-white shadow-sm" : "bg-[#e3e8f0] text-[#a2aab5]"}`}>
						Y
					</span>
					<span className={`flex h-[28px] w-[28px] items-center justify-center rounded-full ${labelPrintAutoValue === "N" ? "bg-white text-[#174a9b] shadow-sm ring-1 ring-inset ring-[#c7cfda]" : "bg-[#e3e8f0] text-[#a2aab5]"}`}>
						N
					</span>
				</button>
			</div>
			<BRDiv />
			<CodeForm
				label={renderDescText(DESC_FLD.INSPECTOR, "검사자")}
				titleWidth={formTitleWidth}
				value={editKSK2140_R01.inspectorEmp}
				custNmValue={editKSK2140_R01.inspectorEmpNm}
				editable={isEditable}
				handleFoucsId="KSK2140_R01-inspector"
				name="inspectorEmp"
				handleChange={getFieldHandlers("inspectorEmp").onChange}
				onDoubleClick={getFieldHandlers("inspectorEmp").onDoubleClick}
				onKeyDown={getFieldHandlers("inspectorEmp").onEnterKeyDown}
			/>
			<CodeForm
				label={renderDescText(DESC_FLD.APPROVER, "승인자")}
				titleWidth={formTitleWidth}
				value={editKSK2140_R01.approvorEmpno}
				custNmValue={editKSK2140_R01.approvorEmpNm}
				editable={isEditable}
				handleFoucsId="KSK2140_R01-approver"
				name="approvorEmpno"
				handleChange={getFieldHandlers("approvorEmpno").onChange}
				onDoubleClick={getFieldHandlers("approvorEmpno").onDoubleClick}
				onKeyDown={getFieldHandlers("approvorEmpno").onEnterKeyDown}
			/>
		</div>
	);
};
